import { useMemo } from 'react';
import * as R from 'remeda';
import { Queue } from '@rossum/api-client/queues';
import { useUnpaginatedEngines } from '../../engines/hooks/useUnpaginatedEngines';
import { useUnpaginatedDedicatedEngines } from '../../legacy-engines/hooks/useUnpaginatedDedicatedEngines';
import { useUnpaginatedGenericEngines } from '../../legacy-engines/hooks/useUnpaginatedGenericEngines';

export type EngineType = 'aurora' | 'generic' | 'dedicated';

export type EngineWithType = {
  id: number;
  name: string;
  engineType: EngineType;
  // Compound key to uniquely identify engine across types (e.g., "aurora-5", "dedicated-5")
  key: string;
};

export type EngineFilters = {
  engine?: number[];
  genericEngine?: number[];
  dedicatedEngine?: number[];
};

export const useAllEngines = () => {
  const { data: auroraEngines, isLoading: isAuroraEnginesLoading } =
    useUnpaginatedEngines();
  const { data: genericEngines, isLoading: isGenericEnginesLoading } =
    useUnpaginatedGenericEngines();
  const { data: dedicatedEngines, isLoading: isDedicatedEnginesLoading } =
    useUnpaginatedDedicatedEngines();

  const isLoading =
    isAuroraEnginesLoading ||
    isGenericEnginesLoading ||
    isDedicatedEnginesLoading;

  const enginesFilterOptions: EngineWithType[] = useMemo(
    () => [
      ...(auroraEngines?.map(e => ({
        id: e.id,
        name: e.name,
        engineType: 'aurora' as const,
        key: `aurora-${e.id}`,
      })) ?? []),
      ...(genericEngines?.map(e => ({
        id: e.id,
        name: e.name,
        engineType: 'generic' as const,
        key: `generic-${e.id}`,
      })) ?? []),
      ...(dedicatedEngines?.map(e => ({
        id: e.id,
        name: e.name,
        engineType: 'dedicated' as const,
        key: `dedicated-${e.id}`,
      })) ?? []),
    ],
    [auroraEngines, genericEngines, dedicatedEngines]
  );

  const enginesByKey = useMemo(
    () => R.indexBy(enginesFilterOptions, R.prop('key')),
    [enginesFilterOptions]
  );

  // Maps for looking up engines by URL (used by getEngineChipData)
  const auroraEnginesMap = useMemo(
    () => R.indexBy(auroraEngines ?? [], R.prop('url')),
    [auroraEngines]
  );

  const genericEnginesMap = useMemo(
    () => R.indexBy(genericEngines ?? [], R.prop('url')),
    [genericEngines]
  );

  const dedicatedEnginesMap = useMemo(
    () => R.indexBy(dedicatedEngines ?? [], R.prop('url')),
    [dedicatedEngines]
  );

  // Separate selected engine keys by type for API filtering
  const transformEngineFiltersToQuery = (
    selectedKeys: string[] | undefined
  ): EngineFilters => {
    if (!selectedKeys?.length) return {};

    const selectedEngines = selectedKeys
      .map(key => enginesByKey[key])
      .filter((e): e is EngineWithType => Boolean(e));

    const aurora = selectedEngines
      .filter(e => e.engineType === 'aurora')
      .map(e => e.id);
    const generic = selectedEngines
      .filter(e => e.engineType === 'generic')
      .map(e => e.id);
    const dedicated = selectedEngines
      .filter(e => e.engineType === 'dedicated')
      .map(e => e.id);

    return {
      engine: aurora.length ? aurora : undefined,
      genericEngine: generic.length ? generic : undefined,
      dedicatedEngine: dedicated.length ? dedicated : undefined,
    };
  };

  // Get engine chip data for a queue (used by QueuesList)
  const getEngineChipData = (queue: Queue) => {
    // Dummy values are not rendered — the chip shows a skeleton when isLoading is true
    if (isLoading)
      return { type: 'generic' as const, name: '', id: 1, isLoading: true };

    const sources = [
      [queue.engine, 'aurora', auroraEnginesMap],
      [queue.genericEngine, 'generic', genericEnginesMap],
      [queue.dedicatedEngine, 'dedicated', dedicatedEnginesMap],
    ] as const;

    const match = sources.find(([url, , map]) => url && map[url]);

    if (!match) return undefined;

    const [url, type, map] = match;
    const engine = map[url!];

    if (!engine) return undefined;

    return { type, name: engine.name, id: engine.id, isLoading: false };
  };

  return {
    enginesFilterOptions,
    isLoading,
    transformEngineFiltersToQuery,
    getEngineChipData,
  };
};
